import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../api/client';
import { can } from '../lib/perms';
import { longDt } from '../lib/filters';

/**
 * Interní zprávy — the legacy `message` table on the dashboard, live.
 *
 * The board shows what is current (release ≤ now ≤ end), newest first,
 * with who wrote it and when. "Nová zpráva" opens the legacy form cut
 * down to what it needed: a title, the text, and how many days it stays
 * up (the legacy default was two). "Smazat" is the legacy delete — a
 * status, not a DELETE — and is offered on your own messages, or on all of
 * them to an operator or admin, the legacy rule.
 */
/** `compact`: one line when the board is empty — the heading, the empty
 *  text and the button share a row — and no more height than the messages
 *  need when it is not. The dashboard uses it since 30 September. */
export function MessagesCard({ children, compact = false }: { children?: React.ReactNode; compact?: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [days, setDays] = useState('2');

  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const q = useQuery({ queryKey: ['messages'], queryFn: () => api.messages(), refetchInterval: 60_000 });
  const canWrite = can(me.data, '/messages/*', 'write');
  const canRemoveAny = me.data?.role === 'admin' || me.data?.role === 'operator';

  const post = useMutation({
    mutationFn: () => api.postMessage({ title: title.trim(), content: content.trim(), days: Number(days) || 2 }),
    onSuccess: async () => {
      setTitle('');
      setContent('');
      setDays('2');
      setOpen(false);
      await qc.invalidateQueries({ queryKey: ['messages'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.deleteMessage(id),
    onSuccess: async () => qc.invalidateQueries({ queryKey: ['messages'] }),
  });

  const msg = (err: unknown) =>
    err instanceof ApiError
      ? err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message)
      : err instanceof Error
        ? err.message
        : null;

  return (
    <div className={compact ? 'card zpravy compact' : 'card zpravy'}>
      <div className="h">
        <h2>Interní zprávy</h2>
        {q.data && q.data.length > 0 && <span className="tab-count">{q.data.length}</span>}
        {compact && !open && q.data && q.data.length === 0 && (
          <span className="empty inline">Žádné zprávy pro tuto směnu.</span>
        )}
        {canWrite && !open && (
          <button type="button" className="btn sm" onClick={() => setOpen(true)}>
            + Nová zpráva
          </button>
        )}
      </div>

      {open && (
        <form
          className="msgform"
          onSubmit={(e) => {
            e.preventDefault();
            if (!post.isPending && title.trim() && content.trim()) post.mutate();
          }}
        >
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nadpis" maxLength={120} autoFocus />
          <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Text pro další směnu" maxLength={4000} rows={3} />
          <div className="msgactions">
            <label>
              Platí
              <input type="number" min={1} max={60} value={days} onChange={(e) => setDays(e.target.value)} /> dní
            </label>
            <button type="submit" className="btn sm primary" disabled={post.isPending || !title.trim() || !content.trim()}>
              {post.isPending ? 'Ukládám…' : 'Zveřejnit'}
            </button>
            <button type="button" className="btn sm" onClick={() => setOpen(false)} disabled={post.isPending}>
              Zrušit
            </button>
            {post.error && <span className="arrive-error" role="alert">{msg(post.error)}</span>}
          </div>
        </form>
      )}

      {q.isPending ? (
        <div className="empty">Načítám…</div>
      ) : q.isError ? (
        <div className="empty" role="alert">Zprávy se nepodařilo načíst.</div>
      ) : q.data.length === 0 ? (
        compact ? null : <div className="empty">Žádné zprávy pro tuto směnu.</div>
      ) : (
        <ul className="msglist">
          {q.data.map((m) => (
            <li key={m.id}>
              <div className="msghead">
                <b>{m.title}</b>
                <span className="msgmeta">
                  {m.userName || `#${m.userId}`} · {longDt(m.releaseDate)} · do {longDt(m.endDate)}
                </span>
                {(canRemoveAny || (me.data && me.data.id === m.userId)) && canWrite && (
                  <button
                    type="button"
                    className="linklike msgdel"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(m.id)}
                    title="Sundat z nástěnky"
                  >
                    smazat
                  </button>
                )}
              </div>
              <div className="msgbody">{m.content}</div>
            </li>
          ))}
          {remove.error && <li className="arrive-error" role="alert">{msg(remove.error)}</li>}
        </ul>
      )}

      {children}
    </div>
  );
}
